import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Compass, ArrowLeft, CheckCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { toast } from "sonner";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) return toast.error("Password must be at least 6 characters");
    if (pwd !== pwd2) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pwd }),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success("Password reset!"); }
      else toast.error(data.detail || "Reset failed");
    } catch (e) { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h2 className="font-inter font-bold text-lg mb-2">Invalid reset link</h2>
          <p className="text-white/50 text-sm mb-4">This reset link is missing a token or has been used.</p>
          <Link to="/forgot-password"><Button className="bg-purple-700 text-white">Request a new link</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-white/40 hover:text-white mb-6 text-sm"><ArrowLeft className="w-4 h-4" />Back to sign in</Link>
        <div className="bg-dark-paper border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></div>
            <span className="font-inter font-bold text-lg">promptviser</span>
          </div>

          {done ? (
            <div className="text-center py-6" data-testid="reset-success">
              <CheckCheck className="w-10 h-10 text-neon mx-auto mb-3" />
              <h2 className="font-inter font-bold text-lg mb-2">Password reset!</h2>
              <p className="text-white/50 text-sm mb-4">You can now sign in with your new password.</p>
              <Button onClick={() => navigate("/login")} className="bg-purple-700 text-white hover:bg-purple-600">Sign in</Button>
            </div>
          ) : (
            <>
              <h1 className="font-inter font-bold text-2xl mb-2">Reset your password</h1>
              <p className="text-white/50 text-sm mb-6">Enter a new password below.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs">New password</Label>
                  <Input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="bg-dark-subtle border-white/10 text-sm" placeholder="At least 6 characters" data-testid="reset-pwd1" />
                </div>
                <div>
                  <Label className="text-xs">Confirm new password</Label>
                  <Input type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="bg-dark-subtle border-white/10 text-sm" data-testid="reset-pwd2" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-purple-700 text-white hover:bg-purple-600" data-testid="reset-submit">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting…</> : "Reset password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
